import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ITimelineEntryWithData } from "@app/directives/_veri/v-timeline/v-timeline.component";
import { LatestSensorDataValues, SensorDataValueType } from "@app/interfaces/ISensorDataValue";
import { IDiagnosticTroubleCode } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticTroubleCode";
import { DateService } from "@app/services/date.service";
import { HardwareSource } from "@interfaces/IVehicleInput";
import { NavigationService } from "@services/navigation.service";
import { TranslationService } from "@services/translation.service";
import { MultiStateInputCategory } from "@temperature/_enums/reefer/MultiStateInputCategory.enum";
import { ReeferMake } from "@temperature/_enums/reefer/ReeferMake.enum";
import { IMultiStateInput, IMultiStateInputWithHistories } from "@temperature/_models/multiStateInputs/IMultiStateInput";
import { IMultiStateInputHistory } from "@temperature/_models/multiStateInputs/IMultiStateInputHistory";
import { IReefer } from "@temperature/_models/reefer/IReefer";
import { IReeferQueryOptions } from "@temperature/_models/reefer/IReeferQueryOptions";
import { IZoneWithProbes } from "@temperature/_models/temperature/ITemperatureZone";
import { DiagnosticTroubleCodeCategory } from "@temperature/_models/troubleCodes/IReeferTroubleCodeHistory";
import { Observable } from "rxjs";
import { IDiagnosticRecord } from "../../maintenance/_components/faultCodes/_models/IDiagnosticRecord";
import { IVehicleWithSensorDataValues } from "../../maintenance/_components/sensorData/_models/IVehicleWithSensorData";
import { IReeferWithMappings } from "../_models/reefer/IReeferWithMappings";
import { ITemperatureRecord } from "../_models/temperature/ITemperatureRecord";

@Injectable({
    providedIn: 'root'
})
export class ReeferService {

    constructor(
        private readonly http: HttpClient,
        private readonly dateService: DateService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService) {
    }

    getReeferData(queryOptions: IReeferQueryOptions = {}): Observable<IReefer[]> {
        return this.http.get<IReefer[]>('/api/reefer', { params: queryOptions as HttpParams });
    }

    santitiseReeferSerialNumber(reeferUnit: IReefer) {
        if (!reeferUnit.SerialNumber) {
            return;
        }

        reeferUnit.SerialNumber = reeferUnit.SerialNumber.replace(/[^a-zA-Z0-9 -]/g, "").trim();

        const allOne = str => /^(.)\1*$/.test(str)

        if (allOne(reeferUnit.SerialNumber)) {
            reeferUnit.SerialNumber = null;
        }
    }

    openReeferDetailsDialogById(vehicleId: number, parentStateName = "app.temperature.reefer"): void {
        this.navigationService.go(`${parentStateName}.view`, { vehicleId: vehicleId });
    }

    openReeferDetailsDialog(vehicleId: number, parentStateName = "app.temperature.reefer"): void {
        this.openReeferDetailsDialogById(vehicleId, parentStateName);
    }

    getLatestReeferMode(inputHistories: IMultiStateInputHistory[], multiStateInputs: IMultiStateInput[], modeType: MultiStateInputCategory, source: HardwareSource): number {
        if (!inputHistories || !inputHistories.length) {
            return;
        }

        const input = multiStateInputs.find(input => input.Category === modeType && input.Source === source);

        if (!input) {
            return;
        }

        const latestHistory = inputHistories.find(history => history.MultiStateInputId === input.Id);

        return latestHistory?.TriggerState;
    }

    getLastUpdated(zones: IZoneWithProbes[], diagnosticRecords: IDiagnosticRecord[]): Date {
        if (!zones?.length) {
            return;
        }

        const latestUpdates = [];
        zones.forEach(zone => {
            zone.probes.forEach(probe => {
                probe.latestRecord && latestUpdates.push(new Date(probe.latestRecord?.Timestamp).getTime());
            })
        });

        if (diagnosticRecords?.length) {
            diagnosticRecords.forEach(record => {
                latestUpdates.push(new Date(record.DateReceived).getTime());
            })
        }

        if (latestUpdates.length > 0) {
            return new Date(Math.max(...latestUpdates));
        }

        return null;
    }

    filterReeferErrorCodes(diagnosticRecords: IDiagnosticRecord[]): IDiagnosticRecord[] {
        return diagnosticRecords.filter(record => {
            return record.Category === DiagnosticTroubleCodeCategory.Carrier || record.Category === DiagnosticTroubleCodeCategory.Ibox;
        })
    }

    getActiveReeferErrors(diagnosticRecords: IDiagnosticRecord[], troubleCodes: IDiagnosticTroubleCode[]): IDiagnosticTroubleCode[] {
        if (!diagnosticRecords?.length || !troubleCodes?.length) {
            return [];
        }

        const currentReeferActiveRecords = this.filterReeferErrorCodes(diagnosticRecords);
        if (!currentReeferActiveRecords.length) {
            return [];
        }

        const currentActiveTroubleCodes: IDiagnosticTroubleCode[] = [];

        currentReeferActiveRecords.forEach(record => {
            const errorCode = troubleCodes.find(code => code.Id === record.DiagnosticTroubleCodeId);
            if (!errorCode) {
                return;
            }

            errorCode.dateRaised = record.DateReceived;
            if (!errorCode.Description) {
                errorCode.Description = this.translationService.t('ReeferList.UnknownError');
            }

            currentActiveTroubleCodes.push(errorCode);
        })

        currentActiveTroubleCodes.sort((a, b) => {
            return b.AlarmLevel - a.AlarmLevel;
        })

        return currentActiveTroubleCodes;
    }

    getHardwareSource(make: ReeferMake): HardwareSource {
        if (!make) {
            return;
        }

        switch (make) {
            case ReeferMake.IBOX:
                return HardwareSource.Ibox;

            case ReeferMake.Carrier:
                return HardwareSource.Carrier
        }
    }

    getOperatingModeForTemperatureRecord(record: ITemperatureRecord, multiStateInputs: IMultiStateInputWithHistories[], zoneId: number): IMultiStateInputHistory {
        if (!multiStateInputs || !record) {
            return;
        }
        const input = multiStateInputs.find(input => input.ZoneId === zoneId);

        const historiesAtTime = input.histories.filter(history => {
            return new Date(history.StartTime).getTime() < new Date(record.Timestamp).getTime();
        });

        historiesAtTime.sort((a, b) => {
            return new Date(b.StartTime).getTime() - new Date(a.StartTime).getTime();
        })

        if (historiesAtTime[0]?.EndTime && new Date(historiesAtTime[0]?.EndTime).getTime() < new Date(record.Timestamp).getTime()) {
            return;
        }

        return historiesAtTime[0];
    }

    setupSensorDataForReefer(reeferUnit: IReeferWithMappings, vehicle: IVehicleWithSensorDataValues, sensorDataValues: LatestSensorDataValues[]): void {
        reeferUnit.vehicle = vehicle;
        reeferUnit.vehicle.latestSensorDataValues = sensorDataValues.find(data => data.vehicleId === reeferUnit.VehicleId);
        reeferUnit.fuelLevel = Math.round(reeferUnit.vehicle.latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxEngineFuelLevel));
        if (reeferUnit.fuelLevel > 100) {
            reeferUnit.fuelLevel = null; // Units without fuel sensors give an error code, usually 127.5
        }
        reeferUnit.voltage = Math.round(reeferUnit.vehicle.latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxBatteryVoltage));
        reeferUnit.engineHours = Math.round(reeferUnit.vehicle.latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxEngineOnDuration)) / 3600;
        reeferUnit.electricHours = Math.round(reeferUnit.vehicle.latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxElectricOnDuration)) / 3600;
        reeferUnit.standbyDuration = Math.round(reeferUnit.vehicle.latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxStandbyOnDuration)) / 3600;
    }

    createTimelineEntriesForDiagnosticErrors(diagnosticRecords: IDiagnosticRecord[], historyStartTime: Date): ITimelineEntryWithData<number>[] {
        const timelineEntries: ITimelineEntryWithData<number>[] = [];

        diagnosticRecords.forEach(record => {
            if (this.dateService.date1IsAfterDate2(record.DateReceived, historyStartTime)) {
                timelineEntries.push({
                    icon: "error",
                    date: record.DateReceived as any,
                    largeDetails: {
                        title: this.translationService.t("ReeferDetail.ErrorRaised"),
                        text: record.description
                    }
                });

                if (record.DateCleared) {
                    timelineEntries.push({
                        icon: "check",
                        date: record.DateCleared as any,
                        largeDetails: {
                            title: this.translationService.t("ReeferDetail.ErrorCleared"),
                            text: record.description
                        }
                    });
                }
            }
        });

        return timelineEntries;
    }
}
